import React from "react";
import {
  FiCalendar,
  FiCamera,
  FiHash,
  FiMail,
  FiPhone,
  FiPlus,
  FiSave,
  FiUser,
} from "react-icons/fi";
import { FaAddressCard } from "react-icons/fa";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";
import AdminField from "../../_shared/ui/AdminField";
import AdminRichTextEditor from "../../_shared/ui/AdminRichTextEditor";

import { toAbsolutePhotoUrl } from "../../../../services/trombinosServices";

import {
  TROMBINOS_FORM_HELPERS,
  TROMBINOS_FUNCTION_SUGGESTIONS,
  TROMBINOS_GROUP_SUGGESTIONS,
  TROMBINOS_ROLE_TYPE_OPTIONS,
} from "../data/trombinosAdminData";

const FALLBACK_PHOTO = "/img/no-image.jpg";

export default function TrombinosFormDrawer({
  open,
  form,
  fieldErrors,
  saving,
  isEdit,
  servicesOptions = [],
  onClose,
  onChange,
  onSave,
  onOpenMediaPicker,
}) {
  const photoSrc = form.photo_url
    ? toAbsolutePhotoUrl(form.photo_url)
    : FALLBACK_PHOTO;

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier le profil" : "Nouveau profil"}
      subtitle={
        isEdit
          ? "Mettez à jour la fiche du trombinoscope."
          : "Ajoutez un élu ou un membre du personnel communal."
      }
      icon={FaAddressCard}
      size="full"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Annuler
          </AdminButton>

          <AdminButton
            icon={isEdit ? FiSave : FiPlus}
            variant="primary"
            loading={saving}
            onClick={onSave}
          >
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
          </AdminButton>
        </div>
      }
    >
      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Identité
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <AdminField label="Nom" required error={fieldErrors.nom}>
                <input
                  value={form.nom}
                  onChange={(event) => onChange("nom", event.target.value)}
                  placeholder="Nom"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Prénom" required error={fieldErrors.prenom}>
                <input
                  value={form.prenom}
                  onChange={(event) => onChange("prenom", event.target.value)}
                  placeholder="Prénom"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField
                label="Fonction"
                required
                error={fieldErrors.fonction}
              >
                <input
                  value={form.fonction}
                  onChange={(event) => onChange("fonction", event.target.value)}
                  placeholder="Maire, adjoint, responsable..."
                  list="trombinos-functions"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />

                <datalist id="trombinos-functions">
                  {TROMBINOS_FUNCTION_SUGGESTIONS.map((fonction) => (
                    <option key={fonction} value={fonction} />
                  ))}
                </datalist>
              </AdminField>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <AdminField label="Type / rôle">
                <select
                  value={form.role_type || ""}
                  onChange={(event) =>
                    onChange("role_type", event.target.value)
                  }
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                >
                  {TROMBINOS_ROLE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </AdminField>

              <AdminField label="Groupe">
                <input
                  value={form.groupe}
                  onChange={(event) => onChange("groupe", event.target.value)}
                  placeholder="Majorité, opposition, direction..."
                  list="trombinos-groups"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />

                <datalist id="trombinos-groups">
                  {TROMBINOS_GROUP_SUGGESTIONS.map((groupe) => (
                    <option key={groupe} value={groupe} />
                  ))}
                </datalist>
              </AdminField>

              <AdminField label="Service rattaché">
                <select
                  value={form.service_id || ""}
                  onChange={(event) =>
                    onChange("service_id", event.target.value)
                  }
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                >
                  <option value="">Aucun service rattaché</option>

                  {servicesOptions.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.label}
                    </option>
                  ))}
                </select>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Contact et mandat
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <AdminField
                label="Email professionnel"
                error={fieldErrors.mail_pro}
              >
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiMail className="text-slate-400" />

                  <input
                    value={form.mail_pro}
                    onChange={(event) =>
                      onChange("mail_pro", event.target.value)
                    }
                    placeholder="prenom.nom@mairie.fr"
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Téléphone professionnel">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiPhone className="text-slate-400" />

                  <input
                    value={form.tel_pro}
                    onChange={(event) =>
                      onChange("tel_pro", event.target.value)
                    }
                    placeholder="05 xx xx xx xx"
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Ordre d’affichage">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiHash className="text-slate-400" />

                  <input
                    type="number"
                    value={form.ordre_affichage}
                    onChange={(event) =>
                      onChange("ordre_affichage", event.target.value)
                    }
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Début de mandat">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiCalendar className="text-slate-400" />

                  <input
                    type="date"
                    value={form.mandat_debut || ""}
                    onChange={(event) =>
                      onChange("mandat_debut", event.target.value)
                    }
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Fin de mandat" error={fieldErrors.mandat_fin}>
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiCalendar className="text-slate-400" />

                  <input
                    type="date"
                    value={form.mandat_fin || ""}
                    onChange={(event) =>
                      onChange("mandat_fin", event.target.value)
                    }
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Présentation
            </h3>

            <div className="mt-4 space-y-4">
              <AdminField label="Présentation courte">
                <textarea
                  value={form.presentation_courte}
                  onChange={(event) =>
                    onChange("presentation_courte", event.target.value)
                  }
                  rows={4}
                  placeholder="Texte court affiché dans la carte..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Bio détaillée">
                <AdminRichTextEditor
                  value={form.bio_html}
                  onChange={(html) => onChange("bio_html", html)}
                  placeholder="Rédigez une présentation détaillée..."
                />
              </AdminField>
            </div>
          </section>
        </div>

        <aside className="space-y-5">
          <div className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              Photo du profil
            </p>

            <div className="mt-4 flex flex-col items-center rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
              <div className="relative h-36 w-36 overflow-hidden rounded-[2rem] border-4 border-white bg-slate-200 shadow-lg dark:border-slate-800 dark:bg-slate-800">
                <img
                  src={photoSrc}
                  alt="Aperçu"
                  className="h-full w-full object-cover"
                  onError={(event) => {
                    event.currentTarget.src = FALLBACK_PHOTO;
                  }}
                />
              </div>

              <AdminButton
                icon={FiCamera}
                variant="secondary"
                size="sm"
                onClick={onOpenMediaPicker}
                className="mt-4"
              >
                Choisir une photo
              </AdminButton>
            </div>

            <AdminField label="URL de la photo">
              <input
                value={form.photo_url}
                onChange={(event) => onChange("photo_url", event.target.value)}
                placeholder="/uploads/..."
                className="mt-3 min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
              />
            </AdminField>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              Publication
            </p>

            <button
              type="button"
              onClick={() => onChange("is_elected", !form.is_elected)}
              className={`mt-3 flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${
                form.is_elected
                  ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
                  : "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300"
              }`}
            >
              <span>
                <span className="block text-sm font-bold">
                  {form.is_elected ? "Élu municipal" : "Personnel communal"}
                </span>

                <span className="mt-0.5 block text-xs opacity-75">
                  Classement du profil dans le trombinoscope.
                </span>
              </span>

              <FiUser />
            </button>

            <button
              type="button"
              onClick={() => onChange("is_visible", !form.is_visible)}
              className={`mt-3 flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${
                form.is_visible
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                  : "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              <span>
                <span className="block text-sm font-bold">
                  {form.is_visible ? "Visible public" : "Masqué du public"}
                </span>

                <span className="mt-0.5 block text-xs opacity-75">
                  Contrôle l’affichage sur le site public.
                </span>
              </span>

              <span
                className={`h-6 w-11 rounded-full p-1 transition ${
                  form.is_visible ? "bg-emerald-500" : "bg-slate-400"
                }`}
              >
                <span
                  className={`block h-4 w-4 rounded-full bg-white transition ${
                    form.is_visible ? "translate-x-5" : ""
                  }`}
                />
              </span>
            </button>
          </div>

          <div className="rounded-[1.7rem] border border-indigo-100 bg-indigo-50 p-4 dark:border-indigo-500/20 dark:bg-indigo-500/10">
            <p className="text-sm font-bold text-indigo-900 dark:text-indigo-200">
              Aide à la saisie
            </p>

            <ul className="mt-3 space-y-2">
              {TROMBINOS_FORM_HELPERS.map((helper) => (
                <li
                  key={helper}
                  className="text-sm leading-6 text-indigo-800 dark:text-indigo-200"
                >
                  • {helper}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </AdminDrawer>
  );
}
